import { useState, useEffect } from 'react';
import { useApp } from './context/AppContext';
import { Header } from './components/Header';
import { MapView } from './components/MapView';
import { YearScrubber } from './components/YearScrubber';
import { TopNBarChartWithDispatch } from './components/TopNBarChart';
import { SparklineGrid } from './components/SparklineGrid';
import { ComparisonChart } from './components/ComparisonChart';
import { StateDetailPanel } from './components/StateDetailPanel';
import { LoadingScreen, ErrorScreen } from './components/LoadingScreen';
import { Footer } from './components/Footer';
import { StoryPage } from './pages/StoryPage';
import { AboutPage } from './pages/AboutPage';

type TabId = 'rankings' | 'overview' | 'compare';
type PageId = 'explorer' | 'story' | 'about';

function TabButton({ 
  active, 
  onClick, 
  children 
}: { 
  active: boolean; 
  onClick: () => void; 
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        px-4 py-2 text-sm font-medium rounded-lg transition-all
        ${active 
          ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30' 
          : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
        }
      `}
    >
      {children}
    </button>
  );
}

function ExplorerPage() {
  const { state } = useApp();
  const [activeTab, setActiveTab] = useState<TabId>('rankings');
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <Header />
      
      {/* Main content */}
      <main className="flex-1 container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Left column - Map */}
          <div className="xl:col-span-7 space-y-6">
            {/* Map container */}
            <div className="card overflow-hidden" style={{ height: '500px' }}>
              <MapView />
            </div>
            
            {/* Year scrubber */}
            <YearScrubber />
          </div>
          
          {/* Right column - Charts */}
          <div className="xl:col-span-5 space-y-6">
            {/* Tab navigation */}
            <div className="flex items-center gap-2 p-1 bg-surface-900/50 rounded-lg">
              <TabButton 
                active={activeTab === 'rankings'} 
                onClick={() => setActiveTab('rankings')}
              >
                Top States
              </TabButton>
              <TabButton 
                active={activeTab === 'overview'} 
                onClick={() => setActiveTab('overview')}
              >
                All States
              </TabButton>
              {state.compareMode && (
                <TabButton 
                  active={activeTab === 'compare'} 
                  onClick={() => setActiveTab('compare')}
                >
                  Comparison
                </TabButton>
              )}
            </div>
            
            {/* Tab content */}
            <div className="animate-fade-in">
              {activeTab === 'rankings' && <TopNBarChartWithDispatch />}
              {activeTab === 'overview' && <SparklineGrid />}
              {activeTab === 'compare' && state.compareMode && <ComparisonChart />}
            </div>
            
            {/* Comparison chart always visible in compare mode */}
            {state.compareMode && activeTab !== 'compare' && state.selectedStates.length > 0 && (
              <ComparisonChart />
            )}
          </div>
        </div>
        
        {/* Mobile-only bottom section */}
        <div className="xl:hidden mt-6">
          {activeTab !== 'overview' && <SparklineGrid />}
        </div>
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* State detail panel (slides in from right) */}
      <StateDetailPanel />
    </div>
  );
}

function App() {
  const { isLoading, error } = useApp();
  const [currentPage, setCurrentPage] = useState<PageId>(() => {
    // Check URL hash for initial page
    const hash = window.location.hash.slice(1);
    if (hash === 'story') return 'story';
    if (hash === 'about') return 'about';
    return 'explorer';
  });
  
  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash === 'story') {
        setCurrentPage('story');
      } else if (hash === 'about') {
        setCurrentPage('about');
      } else {
        setCurrentPage('explorer');
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  // Navigate functions
  const navigateToExplorer = () => {
    window.location.hash = '';
    setCurrentPage('explorer');
  };
  
  // Show loading screen
  if (isLoading) {
    return <LoadingScreen />;
  }
  
  // Show error screen
  if (error) {
    return <ErrorScreen message={error} />;
  }
  
  // Render current page
  if (currentPage === 'story') {
    return <StoryPage onNavigateToExplorer={navigateToExplorer} />;
  }
  
  if (currentPage === 'about') {
    return <AboutPage onNavigateBack={navigateToExplorer} />;
  }
  
  return <ExplorerPage />;
}

export default App;
